import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// Reload page when a webpack chunk fails to load (common in SPAs)
window.addEventListener("error", function (event: any) {
  const chunkFailedMessage = /Loading chunk \d+ failed/;
  if (chunkFailedMessage.test(event?.message || "")) {
    // don't reload on localhost during development
    if (window.location.origin !== "http://localhost:3000") {
      window.location.reload();
    }
  }
});

// -------------------------------
// Helpers and types
// -------------------------------

type NominatimLikeResponse = {
  address: {
    country: string;
    country_code: string;
  };
};

// Try ip-api → nominatim → bigdatacloud
async function getCountryFromCoords(
  latitude: number,
  longitude: number
): Promise<NominatimLikeResponse> {
  const isHttps = window.location.protocol === "https:";
  console.log("getCounteryFromCoords function executed");
  // 1. Try ip-api first (only on http)
  if (!isHttps) {
    try {
      console.log("ip first try")
      const ipApiResponse = await fetch(
        "http://ip-api.com/json/?fields=status,country,countryCode,message"
      );

      if (ipApiResponse.ok) {
        const data = await ipApiResponse.json();
        console.log("ip-api response data:", data);

        if (data.status === "success" && data.country) {
          return {
            address: {
              country: data.country,
              country_code: (data.countryCode || "").toLowerCase(),
            },
          };
        }
      }
    } catch (err) {
      console.warn("ip-api failed, falling back:", err);
    }
  }

  // 2. Try Nominatim (OpenStreetMap)
  try {
    console.log("nominatim second try")
    const osmResp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
    );

    if (osmResp.ok) {
      const osmData = await osmResp.json();

      if (osmData?.address?.country) {
        return {
          address: {
            country: osmData.address.country,
            country_code: osmData.address.country_code,
          },
        };
      }
    }
  } catch (err) {
    console.warn("Nominatim failed:", err);
  }

  // 3. Try BigDataCloud reverse geocode (fallback)
  try {
    console.log("bigDataCloud third try")
    const bdResp = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
    );

    if (bdResp.ok) {
      const bdData = await bdResp.json();

      if (bdData.countryName) {
        return {
          address: {
            country: bdData.countryName,
            country_code: (bdData.countryCode || "").toLowerCase(),
          },
        };
      }
    }
  } catch (err) {
    console.warn("BigDataCloud failed:", err);
  }

  // Last fallback: Unknown (Nominatim-compatible)
  return {
    address: {
      country: "Unknown",
      country_code: "unknown",
    },
  };
}

// Function to convert currency based on user's country
async function convertLocalCurrency(
  country: string,
  price: number,
  baseCurrency: string = "USD"
) {
  try {
    const response = await fetch(
      "https://backend.stagobox.com/api/convert-local-currency/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          base_currency: baseCurrency,
          country: country,
          price: price,
        }),
      }
    );

    if (response.ok) {
      const currencyData = await response.json();
      console.log("Currency conversion data:", currencyData);
      return currencyData;
    } else {
      console.error(
        "Failed to convert currency:",
        response.status,
        response.statusText
      );
      return null;
    }
  } catch (error) {
    console.error("Error converting currency:", error);
    return null;
  }
}

// Geolocation function
function getLocation(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if ("geolocation" in navigator) {
      (navigator as any).geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          console.log("Latitude: " + position.coords.latitude);
          console.log("Longitude: " + position.coords.longitude);
          console.log("Accuracy: " + position.coords.accuracy + " meters");
          resolve(position);
        },
        (error: GeolocationPositionError) => {
          console.error("Geolocation error:", error.message);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      const errorMsg = "Geolocation is not supported by this browser.";
      console.error(errorMsg);
      reject(new Error(errorMsg));
    }
  });
}

// Read stored location safely
const getStoredLocation = () => {
  const locationData = localStorage.getItem("userLocation");
  if (locationData) {
    try {
      return JSON.parse(locationData);
    } catch (err) {
      console.warn("Failed to parse stored userLocation, clearing it.", err);
      localStorage.removeItem("userLocation");
      return null;
    }
  }
  return null;
};

// Try IP-based providers (ip-api on http, then HTTPS-friendly providers)
async function getCountryByIP(): Promise<NominatimLikeResponse | null> {
  const isHttps = window.location.protocol === "https:";
  // 1) ip-api (http) - only if page is http to avoid mixed-content
  if (!isHttps) {
    try {
      const resp = await fetch(
        "http://ip-api.com/json/?fields=status,country,countryCode,message"
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.status === "success" && data.country) {
          return {
            address: {
              country: data.country,
              country_code: (data.countryCode || "").toLowerCase(),
            },
          };
        }
      }
    } catch (err) {
      console.warn("ip-api failed:", err);
    }
  }

  // 2) ipwhois.app (https) - free, no key required
  try {
    const resp = await fetch("https://ipwhois.app/json/");
    if (resp.ok) {
      const data = await resp.json();
      if (data && data.country) {
        return {
          address: {
            country: data.country,
            country_code: (
              data.country_code ||
              data.countryCode ||
              ""
            ).toLowerCase(),
          },
        };
      }
    }
  } catch (err) {
    console.warn("ipwhois failed:", err);
  }

  // 3) ipapi.co (https) - another fallback
  try {
    const resp = await fetch("https://ipapi.co/json/");
    if (resp.ok) {
      const data = await resp.json();
      if (data && data.country_name) {
        return {
          address: {
            country: data.country_name,
            country_code: (data.country_code || "").toLowerCase(),
          },
        };
      }
    }
  } catch (err) {
    console.warn("ipapi.co failed:", err);
  }

  // no IP-based result
  return null;
}

// Init user location with smart reload behaviour
async function initUserLocation() {
  const saved = getStoredLocation();
  const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000; // 24 hours

  if (
    saved &&
    saved.timestamp &&
    Date.now() - saved.timestamp < REFRESH_AFTER_MS
  ) {
    // already fresh
    return saved;
  }

  try {
    // 1) Try fast IP-based geolocation first (no browser permission)
    const ipResult = await getCountryByIP();
    if (ipResult) {
      console.log("Got country from IP provider:", ipResult);

      // Convert currency using country (no coords available when using IP only)
      const currencyInfo = await convertLocalCurrency(
        ipResult.address.country,
        1
      );

      const ipLocationData: any = {
        latitude: null,
        longitude: null,
        accuracy: null,
        country: ipResult.address.country,
        country_code: ipResult.address.country_code,
        timestamp: Date.now(),
        currency: currencyInfo || null,
        source: "ip",
      };

      localStorage.setItem("userLocation", JSON.stringify(ipLocationData));

      const shouldReload =
        !saved || !saved.currency || saved.country !== ipLocationData.country;

      // Avoid reloading in local development
      if (shouldReload) {
        window.location.reload();
      }

      return ipLocationData;
    }

    // 2) If IP lookup failed, fall back to browser geolocation + reverse-geocode
    const position = await getLocation();
    const countryData = await getCountryFromCoords(
      position.coords.latitude,
      position.coords.longitude
    );

    const currencyInfo = await convertLocalCurrency(
      countryData.address.country,
      1
    );

    const locationData: any = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      country: countryData.address.country,
      country_code: countryData.address.country_code,
      timestamp: Date.now(),
      currency: currencyInfo || null,
      source: "geolocation",
    };

    localStorage.setItem("userLocation", JSON.stringify(locationData));

    const shouldReloadGeo =
      !saved || !saved.currency || saved.country !== locationData.country;

    if (shouldReloadGeo && window.location.origin !== "http://localhost:3000") {
      window.location.reload();
    }

    return locationData;
  } catch (error) {
    console.log("Failed to init user location:", error);
    return null;
  }
}

// Run once when DOM is ready. Intentionally run outside React so StrictMode double-mount won't re-trigger logic
if (
  document.readyState === "complete" ||
  document.readyState === "interactive"
) {
  void initUserLocation();
} else {
  document.addEventListener("DOMContentLoaded", () => {
    void initUserLocation();
  });
}

root.render(
  window.location.origin === "http://localhost:3000" ? (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  ) : (
    <React.StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </React.StrictMode>
  )
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
